import { Component, signal, input, output, effect, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { DocumentMetadata } from '@core/models/search.model';

@Component({
  selector: 'app-document-viewer-modal',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    NgxExtendedPdfViewerModule
  ],
  templateUrl: './document-viewer-modal.component.html',
  styleUrls: ['./document-viewer-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentViewerModalComponent {
  document = input<DocumentMetadata | null>(null);
  visible = input<boolean>(false);
  visibleChange = output<boolean>();

  documentUrl = signal<string>('');

  constructor() {
    // Update document URL when document changes
    effect(() => {
      const doc = this.document();
      if (doc) {
        this.documentUrl.set(doc.filePath);
      } else {
        this.documentUrl.set('');
      }
    });
  }

  /**
   * Handle modal close
   */
  onHide(): void {
    this.visibleChange.emit(false);
  }

  /**
   * Download document
   */
  downloadDocument(): void {
    const doc = this.document();
    if (!doc) return;

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = doc.filePath;
    link.download = doc.filename;
    link.click();
  }

  /**
   * Get document type display name
   */
  getDocumentTypeDisplay(): string {
    const doc = this.document();
    if (!doc) return '';

    const typeMap: Record<string, string> = {
      pdf: 'PDF Document',
      word: 'Word Document',
      excel: 'Excel Spreadsheet',
      powerpoint: 'PowerPoint Presentation'
    };

    return typeMap[doc.type] || 'Document';
  }

  /**
   * Format file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Check if document is a PDF
   */
  isPdf(): boolean {
    const doc = this.document();
    return doc?.type === 'pdf';
  }

  /**
   * Get formatted date
   */
  getFormattedDate(date: Date): string {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}
